'use client';

import { useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useAtom, useAtomValue } from 'jotai';
import { toast } from 'sonner';
import { isStreamingAtom, dataDeskOpenAtom } from '@/_state/atoms';
import { useConversationQuery, usePublicConversationQuery } from '@/_state/queries';
import { useDeleteConversation, useUpdateVisibility } from '@/_state/mutations';

function useConversationFromPath() {
	const pathname = usePathname();
	return useMemo(() => {
		const ownedMatch = pathname.match(/^\/conversation\/([^/]+)/);
		if (ownedMatch) return { id: ownedMatch[1], isPublicView: false };

		const shareMatch = pathname.match(/^\/share\/conversation\/([^/]+)/);
		if (shareMatch) return { id: shareMatch[1], isPublicView: true };

		return { id: null, isPublicView: false };
	}, [pathname]);
}

export function useConversationNavbar() {
	const { id: conversationId, isPublicView } = useConversationFromPath();
	const isStreaming = useAtomValue(isStreamingAtom);
	const [isDataDeskOpen, setDataDeskOpen] = useAtom(dataDeskOpenAtom);

	const [copied, setCopied] = useState(false);
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const [showVisibilityDialog, setShowVisibilityDialog] = useState(false);

	const { data: ownedConversation, isPending: isOwnedPending } = useConversationQuery(
		!isPublicView ? conversationId : null
	);
	const { data: publicConversation, isPending: isPublicPending } = usePublicConversationQuery(
		isPublicView ? conversationId : null
	);

	const conversation = isPublicView ? publicConversation : ownedConversation;
	const isPending = isPublicView ? isPublicPending : isOwnedPending;

	const { mutate: deleteConversation, isPending: isDeleting } = useDeleteConversation();
	const { mutate: updateVisibility, isPending: isUpdatingVisibility } = useUpdateVisibility();

	const isPublic = conversation?.isPublic ?? false;
	const actionsDisabled = isDeleting || isStreaming;

	const shareUrl =
		typeof window !== 'undefined' && conversationId
			? `${window.location.origin}/share/conversation/${conversationId}`
			: '';
	const shareText = 'I found this helpful blockchain Q&A, might save you some research time';

	const handleCopyLink = async () => {
		try {
			await navigator.clipboard.writeText(shareUrl);
			setCopied(true);
			toast.success('Link copied to clipboard');
			setTimeout(() => setCopied(false), 2000);
		} catch {
			toast.error('Failed to copy link');
		}
	};

	const handleToggleVisibility = () => {
		if (conversationId) {
			updateVisibility({ id: conversationId, isPublic: !isPublic });
		}
		setShowVisibilityDialog(false);
	};

	const handleDelete = () => {
		if (conversationId) {
			deleteConversation(conversationId);
		}
		setShowDeleteDialog(false);
	};

	const shareToTwitter = () => {
		window.open(
			`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
			'_blank',
			'noopener,noreferrer'
		);
	};

	const shareToTelegram = () => {
		window.open(
			`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
			'_blank',
			'noopener,noreferrer'
		);
	};

	const shareToReddit = () => {
		window.open(
			`https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareText)}`,
			'_blank',
			'noopener,noreferrer'
		);
	};

	return {
		conversationId,
		isPublicView,
		conversation,
		isPending,
		isPublic,
		isUpdatingVisibility,
		actionsDisabled,
		isDataDeskOpen,
		setDataDeskOpen,
		copied,
		showDeleteDialog,
		setShowDeleteDialog,
		showVisibilityDialog,
		setShowVisibilityDialog,
		handleCopyLink,
		handleToggleVisibility,
		handleDelete,
		shareToTwitter,
		shareToTelegram,
		shareToReddit
	};
}
